import React from 'react';
import { Star, ExternalLink, MessageSquare, Award, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import SEO from '@/components/SEO';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import OrganizationSchema from '@/components/schema/OrganizationSchema';

const Reviews = () => {
  const reviewPlatforms = [
    {
      name: 'FreeIndex',
      logo: null,
      rating: '5.0',
      reviewCount: '50+',
      description: 'Trusted independent review platform for UK businesses',
      reviewUrl: 'https://www.freeindex.co.uk/write-review.htm?id=172212',
      viewUrl: 'https://www.freeindex.co.uk/profile(energy-performance-direct)_172212.htm',
      color: 'blue',
      instructions: [
        'Click the "Leave a Review" button below',
        'Log in or create a free FreeIndex account',
        'Rate your experience with stars',
        'Write your honest review and submit'
      ]
    },
    {
      name: 'Buy With Confidence',
      logo: 'https://customer-assets.emergentagent.com/job_energy-direct-revamp/artifacts/gfybwp7x_image.png',
      rating: 'Approved',
      reviewCount: 'Verified',
      description: 'Trading Standards approved business scheme',
      reviewUrl: 'https://www.buywithconfidence.gov.uk/rate-a-trader',
      viewUrl: 'https://www.buywithconfidence.gov.uk/profile/energy-performance-direct/6993/?premises_id=48652',
      color: 'green',
      instructions: [
        'Click "Rate Us on Buy With Confidence"',
        'Enter Trader Name: Energy Performance Direct',
        'Click Next',
        'Specify trade performed as: Energy Assessors – Energy Performance Certificates',
        'Complete your rating and review'
      ]
    },
    {
      name: 'Google Reviews',
      logo: null,
      rating: '4.9',
      reviewCount: '100+',
      description: 'Share your experience on Google',
      reviewUrl: 'https://g.page/r/CSkr5oWL7cPZEAE/review',
      viewUrl: 'https://g.page/r/CSkr5oWL7cPZEAE',
      color: 'red',
      instructions: [
        'Click "Leave a Google Review"',
        'Sign in to your Google account (if prompted)',
        'Choose your star rating (1-5 stars)',
        'Write your feedback',
        'Click "Post" to submit your review'
      ]
    }
  ];

  const testimonials = [
    {
      name: 'Mark Z.',
      location: 'Northwood',
      service: 'Domestic EPC',
      rating: 5,
      text: 'I\'ve used Energy Performance Direct on many occasions and the EPC was done speedily and efficiently as usual. Excellent Service 5 Stars.',
      date: '12 Nov 2020',
      platform: 'FreeIndex'
    },
    {
      name: 'Damian C.',
      location: 'Exeter',
      service: 'Commercial EPC',
      rating: 5,
      text: 'We have used Zak for the past few years and he is prompt and efficient, easy to communicate with and will continue to use him.',
      date: '28 Mar 2018',
      platform: 'FreeIndex'
    },
    {
      name: 'Emma G.',
      location: 'Newton Abbot',
      service: 'Domestic EPC',
      rating: 5,
      text: 'Zak was friendly, professional and efficient. He was able to arrange an appointment very quickly and his turnaround time was fantastic. Thank you very much.',
      date: '21 Dec 2017',
      platform: 'FreeIndex'
    },
    {
      name: 'Andrew H.',
      location: 'Minehead',
      service: 'Commercial EPC',
      rating: 5,
      text: 'I had a Commercial EPC, it was arranged and carried out within the week and the results given without delay. Very competitive price.',
      date: '16 Oct 2017',
      platform: 'FreeIndex'
    },
    {
      name: 'Hugh O.',
      location: 'Exeter',
      service: 'Domestic EPC',
      rating: 5,
      text: 'Excellent service from Zak at Energy Performance Direct - faultless.',
      date: '9 Sep 2016',
      platform: 'FreeIndex'
    },
    {
      name: 'Phil C.',
      location: 'Cullompton',
      service: 'Energy Assessment',
      rating: 5,
      text: 'Efficient, friendly, helpful, knowledgeable and easy to contact! 100%.',
      date: '31 Dec 2014',
      platform: 'FreeIndex'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Customer Reviews | Energy Performance Direct Exeter | EPC Testimonials"
        description="Read verified customer reviews for Energy Performance Direct. 5-star rated EPC assessors in Exeter, Devon, and South West UK. See what our customers say about our energy assessment services."
        keywords="EPC reviews Exeter, energy assessor testimonials, customer feedback Devon, Google reviews EPC, FreeIndex reviews"
        canonical="/reviews"
      />
      <OrganizationSchema />
      <Breadcrumbs items={[
        { name: 'Reviews', path: '/reviews' }
      ]} />
      <Navbar />

      {/* Header */}
      <section className="py-16" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Star className="w-8 h-8 fill-yellow-400 text-yellow-400" />
              <Star className="w-8 h-8 fill-yellow-400 text-yellow-400" />
              <Star className="w-8 h-8 fill-yellow-400 text-yellow-400" />
              <Star className="w-8 h-8 fill-yellow-400 text-yellow-400" />
              <Star className="w-8 h-8 fill-yellow-400 text-yellow-400" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 text-white" data-testid="reviews-heading">
              Customer Reviews
            </h1>
            <p className="text-base sm:text-lg opacity-95 text-white">
              See what our customers say about our energy assessment services across Exeter and the South West UK
            </p>
          </div>
        </div>
      </section>

      {/* Trust Stats */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">200+</div>
              <div className="text-sm text-gray-600">Happy Customers</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">4.9</div>
              <div className="text-sm text-gray-600">Average Rating</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">100%</div>
              <div className="text-sm text-gray-600">Verified Reviews</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">48h</div>
              <div className="text-sm text-gray-600">Average Delivery</div>
            </div>
          </div>
        </div>
      </section>

      {/* Review Platforms */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Leave Us a Review</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Your feedback helps us improve and helps others choose the right energy assessor. 
              Please take a moment to share your experience on any of these platforms.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {reviewPlatforms.map((platform, index) => (
              <Card key={index} className="border-none shadow-xl hover:shadow-2xl transition-all" data-testid={`review-platform-${index}`}>
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    {platform.logo ? (
                      <img src={platform.logo} alt={platform.name} className="h-16 mx-auto mb-4" />
                    ) : (
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-gradient-to-br ${
                        platform.color === 'blue' ? 'from-blue-500 to-blue-600' :
                        platform.color === 'green' ? 'from-green-500 to-green-600' :
                        'from-red-500 to-red-600'
                      }`}>
                        <MessageSquare className="w-8 h-8 text-white" />
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{platform.name}</h3>
                    <div className="flex items-center justify-center gap-1 mb-2">
                      {platform.rating !== 'Approved' && platform.rating !== 'Verified' ? (
                        <>
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                          ))}
                          <span className="ml-2 font-bold text-gray-900">{platform.rating}</span>
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Award className="w-6 h-6 text-green-600" />
                          <span className="font-bold text-green-600">{platform.rating}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{platform.reviewCount}</p>
                    <p className="text-xs text-gray-500">{platform.description}</p>
                  </div>

                  <div className="space-y-4 mb-6">
                    <a href={platform.reviewUrl} target="_blank" rel="noopener noreferrer">
                      <Button 
                        className={`w-full bg-gradient-to-r ${
                          platform.color === 'blue' ? 'from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800' :
                          platform.color === 'green' ? 'from-green-600 to-green-700 hover:from-green-700 hover:to-green-800' :
                          'from-red-600 to-red-700 hover:from-red-700 hover:to-red-800'
                        } text-white shadow-lg`}
                        data-testid={`leave-review-${platform.name.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        Leave a Review
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </Button>
                    </a>
                    <a href={platform.viewUrl} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="w-full">
                        View Our Reviews
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </Button>
                    </a>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-900 mb-3 text-sm">How to Leave a Review:</h4>
                    <ol className="space-y-2 text-xs text-gray-700">
                      {platform.instructions.map((instruction, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="font-bold text-green-600 flex-shrink-0">{idx + 1}.</span>
                          <span>{instruction}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
            <p className="text-lg text-gray-600">
              Real reviews from real customers across the South West UK
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all" data-testid={`testimonial-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.location}</p>
                      <p className="text-xs text-green-600 mt-1">{testimonial.service}</p>
                      <p className="text-xs text-blue-600 mt-1">via {testimonial.platform}</p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Reviews Matter */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Your Review Matters</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
                  <MessageSquare className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Help Others</h3>
                <p className="text-sm text-gray-600">Your experience helps others choose the right energy assessor</p>
              </div>
              <div>
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
                  <Award className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Build Trust</h3>
                <p className="text-sm text-gray-600">Verified reviews build confidence in our services</p>
              </div>
              <div>
                <div className="inline-flex items-center justify-center w-12 h-12 bg-cyan-100 rounded-full mb-3">
                  <CheckCircle className="w-6 h-6 text-cyan-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Improve Service</h3>
                <p className="text-sm text-gray-600">Your feedback helps us continually improve</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4 text-white">Ready to Experience Our Service?</h2>
            <p className="text-lg mb-8 opacity-95 text-white">
              Join hundreds of satisfied customers across the South West UK
            </p>
            <Button 
              onClick={() => window.location.href = '/booking'}
              size="lg"
              className="bg-white text-green-600 hover:bg-gray-100 px-12 py-6 text-lg rounded-full shadow-lg"
              data-testid="reviews-book-now-btn"
            >
              Book Your Assessment
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Reviews;